import { Box, Button, ScrollArea, Text, TextInput } from "@mantine/core";
import { IconCheck, IconSearch } from "@tabler/icons-react";
import { useMemo, useState } from "react";
import { Country } from "../../types";
import styles from "./country-selector.module.css";

interface CountryPickerPanelProps {
  countries: Country[];
  popularCountries: Country[];
  selectedCode?: string;
  onChange: (country: Country) => void;
  compact?: boolean;
  loading?: boolean;
  showPopular?: boolean;
}

export function CountryPickerPanel({
  countries,
  popularCountries,
  selectedCode,
  onChange,
  compact = false,
  loading = false,
  showPopular = true,
}: CountryPickerPanelProps) {
  const [query, setQuery] = useState("");
  const filteredCountries = useMemo(() => {
    const normalized = query.trim().toLocaleLowerCase();
    if (!normalized) return countries;
    return countries.filter(
      (country) =>
        country.countryName.toLocaleLowerCase().includes(normalized) ||
        country.code.toLocaleLowerCase().includes(normalized),
    );
  }, [countries, query]);

  return (
    <Box>
      <TextInput
        aria-label="Search countries"
        placeholder="search jobs in"
        leftSection={<IconSearch size={18} stroke={1.7} />}
        value={query}
        onChange={(event) => setQuery(event.currentTarget.value)}
        size="md"
        radius="md"
        mb="md"
      />

      {showPopular && !query && (
        <PopularCountryGrid
          countries={popularCountries}
          selectedCode={selectedCode}
          onChange={onChange}
        />
      )}

      <Text fw={700} size="sm" mb="xs">
        {query ? "Search results" : "All countries"}
      </Text>
      <ScrollArea.Autosize mah={compact ? 300 : 360} type="auto">
        <div className={styles.countryList}>
          {filteredCountries.map((country) => (
            <CountryButton
              key={country.code}
              country={country}
              selected={country.code === selectedCode}
              onClick={() => onChange(country)}
            />
          ))}
          {!loading && filteredCountries.length === 0 && (
            <Text c="dimmed" size="sm" py="md" ta="center">
              No active countries found
            </Text>
          )}
          {loading && (
            <Text c="dimmed" size="sm" py="md" ta="center">
              Loading countries…
            </Text>
          )}
        </div>
      </ScrollArea.Autosize>
    </Box>
  );
}

export function PopularCountryGrid({
  countries,
  selectedCode,
  onChange,
}: {
  countries: Country[];
  selectedCode?: string;
  onChange: (country: Country) => void;
}) {
  if (countries.length === 0) return null;
  return (
    <Box mb="md">
      <Text fw={700} size="sm" mb="xs">
        Popular countries
      </Text>
      <div className={styles.popularGrid}>
        {countries.map((country) => (
          <CountryButton
            key={country.code}
            country={country}
            selected={country.code === selectedCode}
            onClick={() => onChange(country)}
          />
        ))}
      </div>
    </Box>
  );
}

function CountryButton({
  country,
  selected,
  onClick,
}: {
  country: Country;
  selected: boolean;
  onClick: () => void;
}) {
  return (
    <Button
      className={styles.countryButton}
      variant={selected ? "light" : "default"}
      color="dark"
      justify="space-between"
      fullWidth
      onClick={onClick}
      leftSection={<span className={styles.flag}>{countryFlag(country.code)}</span>}
      rightSection={selected ? <IconCheck size={16} /> : undefined}
      aria-pressed={selected}
    >
      <span className={styles.countryName}>{country.countryName}</span>
    </Button>
  );
}

export function countryFlag(code: string): string {
  const normalized = code.trim().toUpperCase();
  if (!/^[A-Z]{2}$/.test(normalized)) return "🌐";
  return String.fromCodePoint(
    ...[...normalized].map((character) => 127397 + character.charCodeAt(0)),
  );
}
